import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "资讯", to: "/category/news" },
  { label: "专题", to: "/specials" },
  { label: "关于", to: "/about" },
];

type SiteHeaderProps = {
  transparent?: boolean;
};

export default function SiteHeader({ transparent = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = !transparent || scrolled;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    if (!value) {
      navigate("/category/all");
      return;
    }
    navigate(`/category/all?q=${encodeURIComponent(value)}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 text-foreground-900 backdrop-blur"
          : "border-b border-white/10 bg-transparent text-background-50"
      }`}
    >
      <div className="shell flex h-16 items-center justify-between gap-4 md:h-20">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 font-heading text-lg font-bold text-primary-950 md:h-10 md:w-10">
            天
          </span>
          <span className="flex flex-col leading-tight">
            <span
              className={`font-heading text-lg font-bold tracking-wide md:text-xl ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              天马传媒
            </span>
            <span
              className={`text-[9px] tracking-[0.28em] ${
                solid ? "text-foreground-500" : "text-background-200/80"
              }`}
            >
              TIANMA MEDIA
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `relative cursor-pointer whitespace-nowrap px-3 py-2 text-sm transition ${
                  isActive
                    ? solid
                      ? "text-primary-600"
                      : "text-accent-300"
                    : solid
                      ? "text-foreground-700 hover:text-primary-600"
                      : "text-background-100/90 hover:text-accent-300"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full transition ${
                      isActive ? "bg-accent-500" : "bg-transparent"
                    }`}
                  ></span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 transition lg:flex ${
              solid
                ? "border-background-300 bg-background-100/80 focus-within:border-primary-300"
                : "border-white/25 bg-white/10 focus-within:border-accent-300"
            }`}
          >
            <i
              className={`ri-search-line text-sm ${
                solid ? "text-foreground-500" : "text-background-200"
              }`}
            ></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品 / 资讯"
              aria-label="站内搜索"
              className={`w-40 bg-transparent text-sm outline-none xl:w-52 ${
                solid
                  ? "text-foreground-900 placeholder:text-foreground-400"
                  : "text-background-50 placeholder:text-background-300/70"
              }`}
            />
            <button
              type="submit"
              className={`cursor-pointer whitespace-nowrap text-xs ${
                solid ? "text-primary-600" : "text-accent-300"
              }`}
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "关闭导航菜单" : "打开导航菜单"}
            aria-expanded={menuOpen}
            className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border transition lg:hidden ${
              solid ? "border-background-300 text-foreground-800" : "border-white/30 text-background-50"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="shell py-4">
            <form
              onSubmit={handleSearch}
              className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
            >
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视作品 / 资讯"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="cursor-pointer whitespace-nowrap text-xs text-primary-600">
                搜索
              </button>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `cursor-pointer rounded-md px-3 py-2.5 text-sm transition ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}